import LocalizedLink from '@components/Localization/LocalizedLink'
import { OldHeading, Image, OldText, linkStyle } from '@gassan-ui'
import { getUrlSlugInLocale } from '@lib/get-url-slug-in-locale'
import { getAmsterdamTimezone } from '@lib/getAmsterdamTimezone'
import { getKontentImage } from '@lib/kontent'
import { format, fromUnixTime, roundToNearestMinutes } from 'date-fns'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'next-i18next'
import { FC } from 'react'
import styled from 'styled-components'

const Card = styled(motion.a)`
  img {
    transition: opacity 0.2s ease-out;
  }
  &:hover,
  &:focus {
    img {
      opacity: 0.7;
    }
  }
`

Card.defaultProps = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { ease: 'easeOut', duration: 0.3 },
}

const Link = styled.span`
  ${linkStyle};
  line-height: 1.45;
  border-bottom: 1px solid currentColor;
  color: ${(p) => p.theme.colors.tabaccoBrown};
  padding-bottom: 0.25rem;
`

type Props = {
  urlSlug: string
  image?: string
  title: string
  address: string
  country: string
  openendTodayTill: number | null
  openingHoursOverwrite?: string
}

const StoreCard: FC<Props> = ({
  urlSlug,
  image,
  title,
  address,
  country,
  openendTodayTill,
  openingHoursOverwrite,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('other')
  let opened = ''
  const timezone = country === 'singapore' ? '(SGT)' : getAmsterdamTimezone()
  if (openendTodayTill) {
    opened = `${t('todayOpenedUntil')} ${format(
      roundToNearestMinutes(fromUnixTime(openendTodayTill)),
      'HH:mm',
    )} ${timezone}`
  } else {
    opened = t('closedToday')
  }
  return (
    <AnimatePresence>
      <LocalizedLink href={`/${getUrlSlugInLocale('stores', language)}/${urlSlug}`}>
        <Card>
          {image && (
            <Image
              className="aspect-[20/11]"
              placeholderSrc={getKontentImage(image, { width: 50 })}
              sources={[
                getKontentImage(image, { width: 400 }),
                getKontentImage(image, { width: 400 }),
                getKontentImage(image, { width: 600 }),
              ]}
            />
          )}

          <OldHeading
            as="div"
            variant="h4"
            mt={{ _: '1rem', medium: '2rem' }}
            mb={{ _: '1rem', medium: '1rem', large: '1.5rem' }}
          >
            {title}
          </OldHeading>
          <OldText variant="regular" as="div" mb=".5rem">
            {address}
          </OldText>
          <OldText
            variant="regular"
            as="div"
            color="shades.500"
            mb={{ _: '1rem', medium: '1.5rem' }}
          >
            {openingHoursOverwrite ? openingHoursOverwrite : opened}
          </OldText>
          <Link>{t('informationAndOpeningHours')}</Link>
        </Card>
      </LocalizedLink>
    </AnimatePresence>
  )
}

export default StoreCard
